/**
 * 顧客契約 CSV の列ヘッダー (列順固定、docs/specs/admin/data/uploads/customer-contract.md §2)。
 *
 * 列は spec の 12 列に「地点Id」「区分」を足した 14 列と、モック (顧客管理の契約推移タブ) と同じ
 * 末尾の「契約者Id」「契約者名」の 2 列、その後ろの「契約電力の変更」1 列 (適用開始日:契約電力;… の 1 セル)。アップロード側の前段チェック
 * (`CustomerContractCsvUpload`) とダウンロード側の CSV 組み立て (`AdminUploads` / 顧客管理)
 * が同じ列定義を要求するため、`src/models/` に置く単一 SoT とする (どちらか片方だけずれると
 * ダウンロード → 編集 → 再アップロードの往復が壊れる)。
 *
 * 自家発の 3 列 (12〜14 列目)・契約者の 2 列 (15〜16 列目)・契約電力の変更 (17 列目) は、それぞれまとめて省略できる
 * (backend `usecase/customercontract/csv.go` の validateHeader が正)。
 */
import { escapeCsvCell } from '~/libs/csv'

/** 日本語の列名 (17 列)。ダウンロードと雛形はこの全列を出す。 */
export const CUSTOMER_CONTRACT_CSV_COLUMNS = [
  '地点Id',
  '需要家Id',
  '区分',
  'エリア',
  '需要家名',
  '供給地点特定番号',
  '電圧階級',
  '契約電力 (kW)',
  '供給開始日',
  '供給終了日',
  '事業者コード',
  '自家発有無',
  '自家発容量 (kW)',
  '逆調契約 (kW)',
  '契約者Id',
  '契約者名',
  '契約電力の変更',
] as const

export type CustomerContractCsvColumn =
  (typeof CUSTOMER_CONTRACT_CSV_COLUMNS)[number]

/** 英語の列名 (csv-columns.md 英語ヘッダー名の規則)。列順は日本語と完全に対応させる。 */
export const CUSTOMER_CONTRACT_CSV_COLUMNS_EN = [
  'CustomerSupplyPointId',
  'CustomerId',
  'ContractStage',
  'Area',
  'CustomerName',
  'SupplyPointSpecificNumber',
  'VoltageClass',
  'ContractPower',
  'SupplyStartDate',
  'SupplyEndDate',
  'BusinessCode',
  'HasPrivateGeneration',
  'PrivateGenerationCapacity',
  'ReversePowerFlowContract',
  'ContractorId',
  'ContractorName',
  'ContractPowerChanges',
] as const

/** 必ず置く先頭の列数 (事業者コードまで)。 */
const BASE_COLUMN_COUNT = 11
/** 自家発の列数 (まとめて省略できる)。 */
const PRIVATE_GENERATION_COLUMN_COUNT = 3
/** 契約者の列数 (まとめて省略できる)。 */
const CONTRACTOR_COLUMN_COUNT = 2

/** ダウンロード・雛形のヘッダー行 (17 列、日本語)。 */
export const CUSTOMER_CONTRACT_CSV_HEADER =
  CUSTOMER_CONTRACT_CSV_COLUMNS.join(',')

/** 列名 key の 1 行を、列順どおりのカンマ区切りにする。 */
const serializeRow = (
  row: Readonly<Partial<Record<CustomerContractCsvColumn, string>>>,
): string =>
  CUSTOMER_CONTRACT_CSV_COLUMNS.map((col) =>
    escapeCsvCell(row[col] ?? ''),
  ).join(',')

/**
 * 雛形 DL 向け serializer (common.md AC-DUC-031)。記入例が省略する列を許容するため `Partial` で受ける。
 * 区切りはカンマ、改行は `\r\n` (RFC 4180)、末尾に改行は追加しない。
 */
export function serializeCustomerContractCsvTemplateRows(
  rows: ReadonlyArray<Partial<Record<CustomerContractCsvColumn, string>>>,
): string {
  return [CUSTOMER_CONTRACT_CSV_HEADER, ...rows.map(serializeRow)].join('\r\n')
}

/**
 * 入稿済みデータのダウンロード向け serializer (common.md §6 AC-DUC-028/029)。proto 由来の値を
 * 全列マッピングする必要があるため、全列必須の型 (`Record`) で受ける。列を増やすと呼び出し側の
 * row 組み立てが型エラーで追従を要求される。
 */
export function serializeCustomerContractCsvExportRows(
  rows: ReadonlyArray<Record<CustomerContractCsvColumn, string>>,
): string {
  return [CUSTOMER_CONTRACT_CSV_HEADER, ...rows.map(serializeRow)].join('\r\n')
}

/**
 * アップロードが受理するヘッダーの全形式 (自家発の有無 × 契約者の有無 × 契約電力の変更の有無 × 日本語 / 英語 = 16 通り)。
 * `precheckCustomerContractCsv` (frontend 前段チェック) が参照する。
 */
export const CUSTOMER_CONTRACT_CSV_ACCEPTED_HEADERS: readonly string[] = [
  CUSTOMER_CONTRACT_CSV_COLUMNS,
  CUSTOMER_CONTRACT_CSV_COLUMNS_EN,
].flatMap((columns) => {
  const base = columns.slice(0, BASE_COLUMN_COUNT)
  const privateGeneration = columns.slice(
    BASE_COLUMN_COUNT,
    BASE_COLUMN_COUNT + PRIVATE_GENERATION_COLUMN_COUNT,
  )
  const contractor = columns.slice(
    BASE_COLUMN_COUNT + PRIVATE_GENERATION_COLUMN_COUNT,
    BASE_COLUMN_COUNT +
      PRIVATE_GENERATION_COLUMN_COUNT +
      CONTRACTOR_COLUMN_COUNT,
  )
  const kwPeriods = columns.slice(
    BASE_COLUMN_COUNT +
      PRIVATE_GENERATION_COLUMN_COUNT +
      CONTRACTOR_COLUMN_COUNT,
  )
  const groups = [privateGeneration, contractor, kwPeriods]
  // 3 つの任意の列グループの置く / 省略の全組み合わせ (backend の allLayouts と同じ)
  return Array.from({ length: 1 << groups.length }, (_, mask) =>
    [
      ...base,
      ...groups.flatMap((group, i) => (((mask >> i) & 1) === 1 ? group : [])),
    ].join(','),
  )
})
